import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Col, Row } from 'antd'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import costSharingPercentageApi, {
  CostSharingPercentageValues,
} from 'core/application/adminDashboard/benefitDetails/design/productClass/costSharingPercentage'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import FormikDefaultInputField from 'common/components/dataEntry/formik/FormikDefaultInputField'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Alert from 'common/components/feedback/Alert'
import notification from 'common/components/feedback/Notification'
import Icon from 'common/components/general/Icon'

import { commandRequest } from 'common/RequestUtils'

import { BaseComponentProps } from '../../../../common/productClassWizardModal/Common'

import './ProductClassStyles.less'

const formValidator = (translate: any) =>
  Yup.object({
    eECoinsurance: Yup.boolean().nullable().required(translate('general.required')),

    eECoinsuranceAmount: Yup.number()
      .nullable()
      .notRequired()
      .when('eECoinsurance', {
        is: (eECoinsurance: Boolean) => eECoinsurance,
        then: Yup.number()
          .nullable()
          .typeError(translate('admin-dashboard.benefit-details.class-design.errors.between-1-99'))
          .integer(translate('admin-dashboard.benefit-details.class-design.errors.integer-number'))
          .min(1, translate('admin-dashboard.benefit-details.class-design.errors.between-1-99'))
          .max(99, translate('admin-dashboard.benefit-details.class-design.errors.between-1-99'))
          .required(translate('general.required')),
      }),
  })

export interface CostSharingPercentageProps extends CostSharingPercentageValues {
  deductable: boolean
}

const CostSharingPercentage: React.FC<BaseComponentProps<CostSharingPercentageProps>> = (props) => {
  const { bindSubmitFormHandler } = props
  const [costSharing, setCostSharing] = useState<boolean | null>(props.state.eECoinsurance)
  const { t: translate } = useTranslation()
  const { planPeriod } = useParams<any>()

  useEffect(() => {
    props.setIsFormValid(formValidator(translate).isValidSync(props.state))
  }, [])

  const onSubmit = async (values: CostSharingPercentageValues) => {
    const valuesToSubmit: CostSharingPercentageValues = {
      productClassId: props.state.productClassId,
      planPeriodId: planPeriod,
      eECoinsurance: values.eECoinsurance,
      eECoinsuranceAmount: values.eECoinsuranceAmount !== null ? +values.eECoinsuranceAmount : null,
    }
    const response = await commandRequest(() => costSharingPercentageApi(valuesToSubmit))
    if (response.errors) {
      props.setIsSubmiting(false)
      notification.open({
        message: response.errors[0],
        type: 'error',
      })
    } else {
      if (props.editing) {
        props.save()
      } else {
        props.goNext()
      }
    }
  }

  const onRadioChange = (e: any, formik: FormikProps<CostSharingPercentageProps> | any) => {
    if (!e.target.value) {
      formik && formik.setFieldValue('eECoinsuranceAmount', null)
      setCostSharing(false)
    } else {
      setCostSharing(true)
    }
    formik && formik.setFieldValue('eECoinsurance', e.target.value)
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={props.state}
      onSubmit={onSubmit}
      validate={(values) => {
        const isValid: boolean = formValidator(translate).isValidSync(values)
        props.setHasDirtyData(isValid)
        props.setIsFormValid(isValid)
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form autoComplete="off" onSubmit={handleSubmit} className="cost_sharing_percentage">
            <Row>
              <Col span={22}>
                <h5 className="cost_sharing_percentage_info pl-8">
                  {translate('admin-dashboard.benefit-details.class-design.cost-sharing.header')}
                </h5>
              </Col>
            </Row>
            <Row>
              <Col span={22}>
                <Radio.Group
                  name="eECoinsurance"
                  defaultValue={props.state.eECoinsurance}
                  onRadioChange={(e: any) => {
                    onRadioChange(e, formik)
                  }}
                >
                  <Row gutter={10}>
                    <Col span={12}>
                      <Radio.Button className="large radio-with-checkmark cost_sharing_percentage__radio" value={false}>
                        <div className="pl-16 pr-24 content">
                          <h5 className="option_title">
                            {translate('admin-dashboard.benefit-details.class-design.cost-sharing.no-cost-sharing')}
                          </h5>
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col span={12}>
                      <Radio.Button className="large radio-with-checkmark cost_sharing_percentage__radio" value={true}>
                        <div className="pl-16 pr-24 content">
                          <Tooltip
                            title={translate('admin-dashboard.benefit-details.class-design.cost-sharing.yes-tooltip')}
                            position="bottom"
                          >
                            <h5 className="option_title">
                              {translate('admin-dashboard.benefit-details.class-design.cost-sharing.yes-cost-sharing')}
                            </h5>
                          </Tooltip>
                        </div>
                      </Radio.Button>
                    </Col>
                  </Row>
                </Radio.Group>
              </Col>
            </Row>
            {costSharing ? (
              <>
                <Row className="form__row">
                  <Col span={22} className="p-8">
                    <hr />
                  </Col>
                </Row>
                <Row className="pt-8">
                  <Col span={22} className="pl-8 pr-8">
                    <Alert
                      type="info"
                      showIcon
                      message={translate('admin-dashboard.benefit-details.class-design.cost-sharing.alert-header')}
                      description={translate('admin-dashboard.benefit-details.class-design.cost-sharing.alert-desc')}
                    />
                  </Col>
                </Row>
                <Row className="form__row">
                  <Col span={22}>
                    <h5 className="cost_sharing_percentage_info pl-8 pr-8">
                      {translate(
                        'admin-dashboard.benefit-details.class-design.cost-sharing.expense-responsibility-header',
                      )}
                    </h5>
                  </Col>
                </Row>
                <Row className="form__row pl-8">
                  <Col span={11}>
                    <FormikDefaultInputField
                      name="eECoinsuranceAmount"
                      label={translate(
                        'admin-dashboard.benefit-details.class-design.cost-sharing.labels.employee-expense-responsibility',
                      )}
                      suffix={<Icon type="fal fa-percentage" fontAwesome />}
                    />
                  </Col>
                </Row>
              </>
            ) : null}
          </form>
        )
      }}
    </Formik>
  )
}

export default CostSharingPercentage
